import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import moment from 'moment';
import React, {useEffect, useState} from 'react';

import BaseEditableField from 'ui-library/BaseEditableField';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import WarningIcon from 'ui-library/icons/WarningIcon';
import DateField from 'ui-library/inputs/DateField';
import PopoverIcon, {PopoverIconProps} from 'ui-library/PopoverIcon';
import TextValue from 'ui-library/TextValue';
import {readableTime} from 'util/DateUtil';

import DatetimeField from './inputs/DatetimeField';

function formatValueForInitialEdit(value: string | null, editPresentationalFormat?: string): string | null {
  if (value == null || value === '') {
    return null;
  }
  const momentValue = moment(value);
  if (momentValue.isValid() && editPresentationalFormat) {
    return momentValue.format(editPresentationalFormat);
  }
  return value;
}

export interface EditableDateFieldProps {
  isDisabled: boolean;
  onChange: (value: string | null) => void;
  /** If non-empty, a valid initial date/datetime value to display. May be reformatted in UI. */
  value: string | null;
  placeholder?: string | null;
  /* Props for icon to render in view mode next to the date. */
  viewerPopoverIconProps?: PopoverIconProps;
  /* If provided, wait for confirmation from this function before entering edit mode */
  confirmBeforeStartEditing?: () => Promise<boolean>;
  /* Disable and render provided tooltip if non-null */
  disableEditsWithTooltip?: string | null;
  shouldShowWarningIcon?: boolean;
  pickerType: 'date' | 'datetime';
  /**
   * Valid formats for the input date/datetime field. If specified, the caller must provide formats matching
   * the picker type. These will be parsed by Moment.js: details in
   * https://momentjs.com/docs/#/parsing/string-format/. Dates/datetimes not conforming to any of these
   * formats will display with error styling, but that does not block submitting the edit.
   */
  validFormats?: Array<string>;
  /**
   * When editing, preexisting or saved values will convert to this format, by default. If specified, this
   * should be a valid Moment.js format: details in https://momentjs.com/docs/#/parsing/string-format/. This
   * does not affect the read view: that will conform to the user preferences or default for their locale.
   */
  editPresentationalFormat?: string;
}

function EditableDateField(props: EditableDateFieldProps): JSX.Element {
  const [editingValue, setEditingValue] = useState(props.value);
  const [isBeingEdited, setIsBeingEdited] = useState(false);

  useEffect(() => {
    if (!isBeingEdited) {
      setEditingValue(props.value);
    }
  }, [isBeingEdited, props.value]);

  const isDisabled = props.isDisabled || props.disableEditsWithTooltip != null;
  const pickerType = props.pickerType;
  const editableField = (
    <BaseEditableField
      editor={
        pickerType === 'date' ? (
          <DateField
            autoFocus={false}
            labelStyle={DateField.LABEL_STYLES.HIDDEN}
            placeholder="Date"
            onChange={(value) => {
              setEditingValue(value);
            }}
            value={editingValue}
            validDatetimeInputFormats={props.validFormats}
            datetimePresentationalFormat={props.editPresentationalFormat}
          />
        ) : (
          <DatetimeField
            autoFocus={false}
            labelStyle={DateField.LABEL_STYLES.HIDDEN}
            placeholder="Date and time"
            onChange={(value) => {
              setEditingValue(value);
            }}
            value={editingValue}
            validDatetimeInputFormats={props.validFormats}
            datetimePresentationalFormat={props.editPresentationalFormat}
          />
        )
      }
      isBeingEdited={isBeingEdited}
      isCompleteEnabled={true}
      isWritable={!isDisabled}
      viewer={
        <>
          {props.shouldShowWarningIcon && <WarningIcon className={classNames(css(styles.warningIcon))} />}
          <span className={css(styles.viewerWrapper)}>
            <TextValue
              value={
                props.value
                  ? readableTime(props.value, {
                      showTime: pickerType === 'datetime',
                    })
                  : ''
              }
              placeholder={props.placeholder}
            />
            {props.value != null && props.viewerPopoverIconProps != null && (
              <PopoverIcon {...props.viewerPopoverIconProps} />
            )}
          </span>
        </>
      }
      onCancel={() => {
        setIsBeingEdited(false);
        setEditingValue(props.value);
      }}
      onComplete={() => {
        props.onChange(editingValue);
        setIsBeingEdited(false);
        setEditingValue(props.value);
      }}
      onEdit={async () => {
        if (props.confirmBeforeStartEditing != null) {
          const shouldProceed = await props.confirmBeforeStartEditing();
          if (!shouldProceed) {
            return;
          }
        }
        setEditingValue(formatValueForInitialEdit(props.value, props.editPresentationalFormat));
        setIsBeingEdited(true);
      }}
    />
  );
  return (
    <Tooltip
      text={props.disableEditsWithTooltip}
      isDisabled={!props.disableEditsWithTooltip}
      // Tooltip displayed below the field if another popover could be present to avoid conflicts.
      placement={props.viewerPopoverIconProps != null ? FloatingPlacement.BOTTOM : undefined}
    >
      <div>{editableField}</div>
    </Tooltip>
  );
}

EditableDateField.defaultProps = {
  isDisabled: false,
  pickerType: 'date',
};

export default EditableDateField;

const styles = StyleSheet.create({
  viewerWrapper: {
    display: 'flex',
  },
  warningIcon: {
    marginRight: '5px',
  },
});
